/* ─────────────────────────────────────────────────────────────────────────────
   WIT Header interactions
   - Mega menu: open/close on click of [data-mega-target] (toggle), click-outside/Esc to close
   - Mobile drawer: open/close + 3-level drilldown
   - Search overlay: open/close
   ───────────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  const HEADER =
    document.querySelector('.wit-section-header') ||
    document.querySelector('sticky-header') ||
    document.querySelector('.section-header');
  if (!HEADER) return;

  // Ensure the open-state attribute selectors can match by guaranteeing the
  // class is present even if a previous header.liquid version is in place.
  HEADER.classList.add('wit-section-header');

  const CONFIG = (() => {
    try {
      return JSON.parse(document.getElementById('WitHeaderConfig').textContent);
    } catch (_) {
      return {};
    }
  })();

  /* ─── Helper: set --header-height var so overlays position correctly ─── */
  function updateHeaderHeightVar() {
    const h = HEADER.offsetHeight;
    // Never write a 0 (or negative) height. offsetHeight can momentarily read 0
    // right as a panel opens (the header is being switched to position:fixed);
    // writing 0 collapses the mega panel's top padding and its columns overlap
    // the nav bar (seen in Safari). Skip the bad reading and keep the last good
    // value (or the CSS 75px fallback) instead.
    if (h > 0) {
      document.documentElement.style.setProperty('--header-height', `${h}px`);
    }
  }
  updateHeaderHeightVar();
  window.addEventListener('resize', updateHeaderHeightVar);
  // Update once promo bar/images load
  window.addEventListener('load', updateHeaderHeightVar);

  /* ─────────────────────────────────────────────────────────────────────────
     MEGA MENU
     ───────────────────────────────────────────────────────────────────────── */
  const megaTriggers = document.querySelectorAll('[data-mega-target]');
  const megaPanels = document.querySelectorAll('.wit-mega-menu[data-mega-id]');
  const megaById = new Map();
  megaPanels.forEach((panel) => {
    const id = panel.getAttribute('data-mega-id');
    if (id) megaById.set(id.trim(), panel);
    // Ensure panel is in DOM but visually closed
    panel.removeAttribute('hidden');
  });

  let activeMega = null;

  /* Drop the decorative nav banner only when this panel's content actually
     doesn't fit the viewport — measured directly rather than guessed from a
     viewport-height breakpoint, since the fit depends on how many links each
     panel's tallest column has (varies per menu) and on real header height,
     which a fixed px threshold can't account for reliably. Re-measures
     against the full (banner-shown) layout each time so it can also revert
     once there's room again. */
  function fitMegaPanel(panel) {
    const inner = panel.querySelector('.wit-mega-menu__inner');
    if (!inner) return;
    panel.classList.remove('wit-mega-menu--tight');
    const overflows = inner.scrollHeight > panel.clientHeight;
    panel.classList.toggle('wit-mega-menu--tight', overflows);
  }

  function fitAllMegaPanels() {
    megaPanels.forEach(fitMegaPanel);
  }

  fitAllMegaPanels();
  window.addEventListener('load', fitAllMegaPanels);
  let megaFitResizeTimer = null;
  window.addEventListener('resize', () => {
    clearTimeout(megaFitResizeTimer);
    megaFitResizeTimer = setTimeout(fitAllMegaPanels, 150);
  });

  function setBodyOpen(isOpen) {
    document.body.classList.toggle('wit-panel-open', !!isOpen);
    applyOpenStylesInline(!!isOpen);
  }

  // Nuclear option: physically force the open/closed look via inline styles on
  // every ancestor in the header DOM chain. Some of the cream background lives
  // on the inner <sticky-header> (via .gradient class), some on the outer
  // .shopify-section wrapper. Both need transparency to let the panel show
  // through. Inline styles bypass CSS specificity AND Shopify CDN caching.
  function applyOpenStylesInline(open) {
    if (!HEADER) return;

    // Build the chain of elements that might own the cream background:
    // outer Shopify section wrapper → sticky-header → wit-header
    const witHeader = HEADER.querySelector('.wit-header');
    const stickyHeader =
      HEADER.tagName.toLowerCase() === 'sticky-header'
        ? HEADER
        : HEADER.querySelector('sticky-header');
    const sectionWrapper =
      HEADER.closest('.shopify-section') ||
      (HEADER.classList.contains('shopify-section') ? HEADER : null);

    const chain = [sectionWrapper, HEADER, stickyHeader, witHeader].filter(
      (el, i, arr) => el && arr.indexOf(el) === i
    );

    if (open) {
      // Kill the cream/light bg on every ancestor so the dark panel shows through
      chain.forEach((el) => {
        el.style.background = 'transparent';
        el.style.backgroundColor = 'transparent';
        el.style.backgroundImage = 'none';
        el.style.borderBottomColor = 'transparent';
      });

      // Float the header above the panel
      HEADER.style.position = 'fixed';
      HEADER.style.top = '0';
      HEADER.style.left = '0';
      HEADER.style.right = '0';
      HEADER.style.zIndex = '10000';
      HEADER.style.transform = 'none';
      HEADER.style.opacity = '1';
      HEADER.style.visibility = 'visible';

      // White text on all interactive/text descendants
      HEADER.querySelectorAll('a, button, span, p, li, div').forEach((el) => {
        el.style.color = '#ffffff';
      });

      // Drop the brightness-0 filter on any logo image
      HEADER.querySelectorAll('img').forEach((img) => {
        img.style.filter = 'none';
      });
    } else {
      // Reset every touched style
      chain.forEach((el) => {
        el.style.background = '';
        el.style.backgroundColor = '';
        el.style.backgroundImage = '';
        el.style.borderBottomColor = '';
      });

      HEADER.style.position = '';
      HEADER.style.top = '';
      HEADER.style.left = '';
      HEADER.style.right = '';
      HEADER.style.zIndex = '';
      HEADER.style.transform = '';
      HEADER.style.opacity = '';
      HEADER.style.visibility = '';

      HEADER.querySelectorAll('a, button, span, p, li, div').forEach((el) => {
        el.style.color = '';
      });

      HEADER.querySelectorAll('img').forEach((img) => {
        img.style.filter = '';
      });
    }
  }

  function openMega(panel, trigger) {
    if (!panel) return;
    if (activeMega && activeMega !== panel) {
      closeMegaImmediately(activeMega);
    }
    // Re-measure right before opening — Shopify's native sticky-header
    // component also writes --header-height (sections/header.liquid), and
    // whichever of the two writers runs last on page load wins. Recomputing
    // here guarantees the panel's top padding matches the header's actual
    // current height regardless of that race.
    updateHeaderHeightVar();
    fitMegaPanel(panel);
    panel.setAttribute('data-open', 'true');
    panel.setAttribute('aria-hidden', 'false');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    HEADER.setAttribute('data-mega-open', 'true');
    setBodyOpen(true);
    activeMega = panel;
  }

  function closeMegaImmediately(panel) {
    if (!panel) return;
    panel.removeAttribute('data-open');
    panel.setAttribute('aria-hidden', 'true');
    const id = panel.getAttribute('data-mega-id');
    document.querySelectorAll(`[data-mega-target="${id}"]`).forEach((t) =>
      t.setAttribute('aria-expanded', 'false')
    );
    if (activeMega === panel) {
      activeMega = null;
      HEADER.removeAttribute('data-mega-open');
      // Only clear body class if no other panel is open
      if (!HEADER.hasAttribute('data-drawer-open') && !HEADER.hasAttribute('data-search-open')) {
        setBodyOpen(false);
      }
    }
  }

  megaTriggers.forEach((trigger) => {
    const target = trigger.getAttribute('data-mega-target');
    const panel = megaById.get(target);
    if (!panel) return;

    trigger.addEventListener('click', () => {
      if (activeMega === panel) {
        closeMegaImmediately(panel);
      } else {
        openMega(panel, trigger);
      }
    });
  });

  megaPanels.forEach((panel) => {
    // Close on explicit close button
    panel.querySelectorAll('[data-wit-mega-close]').forEach((btn) => {
      btn.addEventListener('click', () => closeMegaImmediately(panel));
    });

    // Close on backdrop click. The panel now fills the viewport (see
    // component-wit-mega-menu.css), so its empty dark area sits *inside* the
    // panel — the document-level "click outside" handler no longer covers it.
    // Treat a click on the panel itself, or on the blank space of the inner
    // container (i.e. not on a link/button/content element), as a dismiss.
    panel.addEventListener('click', (e) => {
      if (e.target === panel || e.target.classList.contains('wit-mega-menu__inner')) {
        closeMegaImmediately(panel);
      }
    });
  });

  // Esc closes any open mega
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && activeMega) {
      closeMegaImmediately(activeMega);
    }
  });

  // Click outside the header (and outside the open panel) closes it
  document.addEventListener('click', (e) => {
    if (!activeMega) return;
    if (HEADER.contains(e.target) || activeMega.contains(e.target)) return;
    closeMegaImmediately(activeMega);
  });

  /* ─────────────────────────────────────────────────────────────────────────
     MOBILE DRAWER
     ───────────────────────────────────────────────────────────────────────── */
  const drawer = document.querySelector('[data-wit-drawer]');
  const drawerToggle = document.querySelector('[data-wit-drawer-toggle]');

  if (drawer && drawerToggle) {
    drawer.removeAttribute('hidden');
    const stack = drawer.querySelector('[data-wit-drawer-stack]');
    const panels = drawer.querySelectorAll('.wit-drawer__panel');
    const history = ['root'];

    function setActivePanel(key) {
      panels.forEach((p) => {
        p.classList.remove('wit-drawer__panel--active');
        p.removeAttribute('data-drawer-state');
      });
      const next = drawer.querySelector(`.wit-drawer__panel[data-drawer-key="${key}"]`);
      if (next) {
        next.classList.add('wit-drawer__panel--active');
        if (stack) stack.setAttribute('data-active-key', key);
      }
    }

    function openDrawer() {
      drawer.setAttribute('data-open', 'true');
      drawer.setAttribute('aria-hidden', 'false');
      drawerToggle.setAttribute('aria-expanded', 'true');
      HEADER.setAttribute('data-drawer-open', 'true');
      setBodyOpen(true);
      document.body.style.overflow = 'hidden';
    }

    function closeDrawer() {
      drawer.removeAttribute('data-open');
      drawer.setAttribute('aria-hidden', 'true');
      drawerToggle.setAttribute('aria-expanded', 'false');
      HEADER.removeAttribute('data-drawer-open');
      if (!HEADER.hasAttribute('data-mega-open') && !HEADER.hasAttribute('data-search-open')) {
        setBodyOpen(false);
      }
      document.body.style.overflow = '';
      // Reset to root after close finishes so the next open starts fresh
      setTimeout(() => {
        setActivePanel('root');
        history.length = 0;
        history.push('root');
      }, 300);
    }

    drawerToggle.addEventListener('click', () => {
      if (drawer.getAttribute('data-open') === 'true') {
        closeDrawer();
      } else {
        openDrawer();
      }
    });

    drawer.querySelectorAll('[data-wit-drawer-close]').forEach((btn) => {
      btn.addEventListener('click', closeDrawer);
    });

    drawer.querySelectorAll('[data-wit-drawer-drill]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = btn.getAttribute('data-wit-drawer-drill');
        if (key) {
          history.push(key);
          setActivePanel(key);
        }
      });
    });

    drawer.querySelectorAll('[data-wit-drawer-back]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (history.length > 1) {
          history.pop();
          setActivePanel(history[history.length - 1]);
        }
      });
    });

    // Esc closes drawer
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.getAttribute('data-open') === 'true') {
        closeDrawer();
      }
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     SEARCH OVERLAY
     ───────────────────────────────────────────────────────────────────────── */
  const searchOverlay = document.querySelector('[data-wit-search-overlay]');
  const searchToggles = document.querySelectorAll('[data-wit-search-toggle]');
  const searchInput = searchOverlay && searchOverlay.querySelector('[data-wit-search-input]');

  if (searchOverlay) {
    searchOverlay.removeAttribute('hidden');

    function openSearch() {
      searchOverlay.setAttribute('data-open', 'true');
      searchOverlay.setAttribute('aria-hidden', 'false');
      searchToggles.forEach((t) => t.setAttribute('aria-expanded', 'true'));
      HEADER.setAttribute('data-search-open', 'true');
      setBodyOpen(true);
      // Hide ONLY the inner sticky-header element (the bar with logo/nav/icons).
      // We DO NOT touch the outer .shopify-section wrapper because the search
      // overlay snippet is rendered inside that wrapper — hiding the wrapper
      // would hide the overlay too.
      document.body.classList.add('wit-search-open');
      // Reset the "interacted" lock so empty-centered state shows again next open
      searchOverlay.removeAttribute('data-interacted');
      if (searchInput) setTimeout(() => searchInput.focus(), 60);
      if (activeMega) closeMegaImmediately(activeMega);
    }
    function closeSearch() {
      searchOverlay.removeAttribute('data-open');
      searchOverlay.setAttribute('aria-hidden', 'true');
      searchToggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      HEADER.removeAttribute('data-search-open');
      document.body.classList.remove('wit-search-open');
      searchOverlay.removeAttribute('data-interacted');
      if (!HEADER.hasAttribute('data-mega-open') && !HEADER.hasAttribute('data-drawer-open')) {
        setBodyOpen(false);
      }
    }

    /* Once the user focuses or interacts with the input, lock the layout to
       top-aligned for the rest of the overlay session. Without this, clicking
       the × reset button (which empties the input) causes the
       :has(:not(:placeholder-shown)) rule to flip and the pill snaps back to
       vertical center — which feels janky.

       Mobile note: programmatic focus() from openSearch() often does NOT fire
       a 'focus' event reliably on mobile Safari/Chrome. So we layer multiple
       triggers — focus, input, touchstart, and reset-click — to make sure
       data-interacted gets set on any user interaction. */
    if (searchInput) {
      const lockInteracted = () => searchOverlay.setAttribute('data-interacted', 'true');
      searchInput.addEventListener('focus', lockInteracted);
      searchInput.addEventListener('input', lockInteracted);
      searchInput.addEventListener('touchstart', lockInteracted, { passive: true });
      searchInput.addEventListener('click', lockInteracted);
      // Also when the user clicks the × reset (so even an edge-case clear
      // doesn't unlock the layout)
      const resetBtn = searchOverlay.querySelector('.wit-search-overlay__reset');
      if (resetBtn) resetBtn.addEventListener('click', lockInteracted);
    }

    searchToggles.forEach((btn) =>
      btn.addEventListener('click', () => {
        if (searchOverlay.getAttribute('data-open') === 'true') closeSearch();
        else openSearch();
      })
    );

    searchOverlay.querySelectorAll('[data-wit-search-close]').forEach((btn) =>
      btn.addEventListener('click', closeSearch)
    );

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && searchOverlay.getAttribute('data-open') === 'true') {
        closeSearch();
      }
    });

    /* NOTE: Predictive search is handled by Shopify's native <predictive-search>
       custom element (assets/predictive-search.js). The overlay snippet wraps
       the input in that element, which AJAX-fetches sections/predictive-search.liquid
       on input. We just style the output via component-wit-header.css overrides.

       The below predictive-search code is now disabled. Restored fallback only if
       there's no <predictive-search> element wrapping the input (i.e. theme has
       predictive_search_enabled off). */
    if (searchInput && !searchInput.closest('predictive-search')) {
      const resultsEl = searchOverlay.querySelector('[data-wit-search-results]');
      let suggestTimer = null;

      const escapeHtml = (str) =>
        String(str).replace(/[&<>"']/g, (c) => ({
          '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
        }[c]));

      // Bold the part of the suggestion that the user hasn't typed yet
      const highlightSuggestion = (text, query) => {
        const t = String(text);
        const q = String(query).trim().toLowerCase();
        if (!q) return escapeHtml(t);
        const idx = t.toLowerCase().indexOf(q);
        if (idx === -1) return `<strong>${escapeHtml(t)}</strong>`;
        const before = t.slice(0, idx);
        const match = t.slice(idx, idx + q.length);
        const after = t.slice(idx + q.length);
        return [
          before ? `<strong>${escapeHtml(before)}</strong>` : '',
          escapeHtml(match),
          after ? `<strong>${escapeHtml(after)}</strong>` : ''
        ].join('');
      };

      const renderSection = (title, items, renderItem) => {
        if (!items || items.length === 0) return '';
        return `
          <div class="wit-search-overlay__group">
            <p class="wit-search-overlay__group-title">${title}</p>
            <ul class="wit-search-overlay__group-list">
              ${items.map(renderItem).join('')}
            </ul>
          </div>`;
      };

      const renderResults = (data, query) => {
        if (!resultsEl) return;
        const r = (data && data.resources && data.resources.results) || {};
        const queries = r.queries || [];
        const products = r.products || [];
        const pages = (r.pages || []).concat(r.articles || []);
        const collections = r.collections || [];

        const suggestionItems = queries.concat(collections.map((c) => ({ text: c.title, url: c.url })));

        let html = '';
        html += renderSection('SUGGESTIONS', suggestionItems, (item) => {
          const text = item.text || item.title || '';
          const url = item.url || `/search?q=${encodeURIComponent(text)}`;
          return `<li><a class="wit-search-overlay__suggestion" href="${url}">${highlightSuggestion(text, query)}</a></li>`;
        });

        html += renderSection('PRODUCTS', products, (p) => {
          const img = p.image || (p.featured_image && p.featured_image.url) || '';
          const imgTag = img
            ? `<span class="wit-search-overlay__product-thumb"><img src="${img}" alt="" loading="lazy"></span>`
            : `<span class="wit-search-overlay__product-thumb wit-search-overlay__product-thumb--empty" aria-hidden="true"></span>`;
          return `<li><a class="wit-search-overlay__product" href="${p.url}">${imgTag}<span class="wit-search-overlay__product-title">${escapeHtml(p.title)}</span></a></li>`;
        });

        html += renderSection('PAGES', pages, (pg) => {
          return `<li><a class="wit-search-overlay__page" href="${pg.url}">${escapeHtml(pg.title)}</a></li>`;
        });

        // "Search for X" link at the bottom
        if (query) {
          html += `<a class="wit-search-overlay__searchall" href="/search?q=${encodeURIComponent(query)}">
            <span>Search for &ldquo;${escapeHtml(query)}&rdquo;</span>
            <span aria-hidden="true" class="wit-search-overlay__searchall-arrow">&rarr;</span>
          </a>`;
        }

        if (!html) {
          resultsEl.innerHTML = '<p class="wit-search-overlay__empty">No results.</p>';
          return;
        }
        resultsEl.innerHTML = html;
      };

      searchInput.addEventListener('input', () => {
        clearTimeout(suggestTimer);
        const q = searchInput.value.trim();
        if (!q || q.length < 2) {
          if (resultsEl) resultsEl.innerHTML = '';
          return;
        }
        suggestTimer = setTimeout(() => {
          const types = 'query,product,collection,page,article';
          fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=${types}&resources[limit]=5&resources[options][unavailable_products]=last`)
            .then((r) => r.json())
            .then((data) => renderResults(data, q))
            .catch(() => {});
        }, 180);
      });
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
     LOCALIZATION (country / currency / language) SELECTOR
     snippets/wit-localization.liquid (context 'header'): the [GBP] pill opens a
     dropdown of country (and, on eu., language) buttons. This only opens and
     closes it; choosing an item is handled by assets/wit-market.js, which
     submits through the shared #WitMarketForm.
     ───────────────────────────────────────────────────────────────────────── */
  (function initLocalizationSelector() {
    const root = document.querySelector('[data-wit-loc]');
    if (!root) return;
    const toggle = root.querySelector('.wit-header__localization-toggle');
    const panel = root.querySelector('[data-wit-loc-panel]');
    if (!toggle || !panel) return;

    const isOpen = () => toggle.getAttribute('aria-expanded') === 'true';

    function setOpen(open, returnFocus) {
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) return;
      // Drop any half-answered "switch anyway?" warning
      const confirm = panel.querySelector('[data-wit-loc-confirm]');
      if (confirm) {
        confirm.hidden = true;
        confirm.textContent = '';
      }
      if (returnFocus) toggle.focus();
    }

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      setOpen(!isOpen());
    });
    document.addEventListener('click', function (e) {
      if (isOpen() && !root.contains(e.target)) setOpen(false);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) setOpen(false, true);
    });
    root.addEventListener('focusout', function (e) {
      if (isOpen() && e.relatedTarget && !root.contains(e.relatedTarget)) setOpen(false);
    });
  })();
})();